import { useEffect, useMemo, useState } from 'react';
import { Hash, Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { CHAT_SEARCH_MAX_QUERY_LENGTH, chatSearchMinQueryLength } from '@fias/platform-resources';
import { UNNAMED_CHANNEL_LABEL } from '@/lib/chat-display';
import { useChatGlobalSearch } from '../hooks/useChatGlobalSearch';
import { useChatMessageSearch } from '../hooks/useChatMessageSearch';
import { useChatSearchControls } from '../hooks/useChatSearchControls';
import { useChatConversationSearch } from '../hooks/useChatConversationSearch';
import { useChatPeopleSearch } from '../hooks/useChatPeopleSearch';
import {
  channelDisplayName,
  filterChannelsByQuery,
  searchHitChannelLabel,
  seededGradient,
} from '../utils';
import {
  NO_MATCH_HINT,
  operatorHint,
  searchTermsForHighlight,
  type AbsorbableOperator,
} from './message-search-utils';
import { MessageSearchResultRow } from './MessageSearchResultRow';
import { SenderFilter } from './SenderFilter';
import { SearchMatchModeToggle } from './SearchMatchModeToggle';
import { SearchHasFilter } from './SearchHasFilter';
import { SearchDateFilter } from './SearchDateFilter';
import { SearchErrorState } from './SearchErrorState';
import { CHAT_SEARCH_DEBOUNCE_MS } from './ChannelSearchPanel';
import { ChannelAvatar } from './ChannelAvatar';
import { UserAvatar } from './UserAvatar';
import type {
  ChatChannelResponse,
  ChatConversationSearchHit,
  ChatMessageSearchHitResponse,
  ChatPeopleSearchHit,
} from '@/lib/api';

/** Across conversations every operator applies, `in:` included. */
const GLOBAL_OPERATORS: ReadonlySet<AbsorbableOperator> = new Set([
  'from',
  'in',
  'after',
  'before',
  'has',
]);

const OPERATOR_HINT = operatorHint(GLOBAL_OPERATORS);

/** Conversation and people sections show this many before "Show all". */
const SECTION_PREVIEW_SIZE = 8;

/** One row in the Conversations section, whether it came from memory or the server. */
interface ConversationRow {
  channelId: string;
  channelType: ChatChannelResponse['channelType'];
  label: string;
  subtitle: string | null;
  /** The loaded channel when there is one (drives the avatar); server hits have none. */
  channel: ChatChannelResponse | null;
}

/** The conversation an `in:` operator resolved to. */
interface InChannel {
  channelId: string;
  channelType: ChatChannelResponse['channelType'];
  /** Labelled like a search hit: "#general", or a DM partner's name unprefixed. */
  label: string;
}

interface AllChatsSearchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Text to start with — the sidebar filter hands its query over here. */
  initialQuery?: string;
  /** The loaded conversation list, matched locally for instant results. */
  channels: ChatChannelResponse[];
  /** Guild names, for labelling guild channels and matching by guild. */
  guilds: ReadonlyArray<{ guildId: string; name: string }>;
  /**
   * Label for the conversation a hit came from (e.g. a DM partner's name or
   * "#general"). Supplied by the page, which already holds the channel list.
   */
  resolveChannelLabel: (hit: ChatMessageSearchHitResponse) => string;
  /** Open the hit's conversation and jump to the message. */
  onSelectResult: (hit: ChatMessageSearchHitResponse) => void;
  /** Open a conversation from the Conversations section. */
  onSelectConversation: (channelId: string) => void;
  /** Open (or start) a direct message with a person from the People section. */
  onSelectPerson: (hit: ChatPeopleSearchHit) => void;
}

function conversationRowFromChannel(
  channel: ChatChannelResponse,
  guildNameById: Map<string, string>,
): ConversationRow {
  return {
    channelId: channel.channelId,
    channelType: channel.channelType,
    label: channelDisplayName(channel),
    subtitle: channel.guildId ? (guildNameById.get(channel.guildId) ?? null) : null,
    channel,
  };
}

function conversationRowFromHit(
  hit: ChatConversationSearchHit,
  guildNameById: Map<string, string>,
): ConversationRow {
  return {
    channelId: hit.channelId,
    channelType: hit.channelType,
    label: hit.name || UNNAMED_CHANNEL_LABEL,
    subtitle: hit.guildId ? (guildNameById.get(hit.guildId) ?? null) : null,
    channel: null,
  };
}

/**
 * Resolves an `in:` name to one conversation: a UNIQUE exact (case-insensitive)
 * match on the shown name first, then a UNIQUE substring match — `in:gen` is
 * `#general` when nothing else contains "gen". Two candidates at either level
 * (two guilds each with a `general`, say) resolve to nothing rather than to
 * whichever happened to load first: a search silently scoped to the wrong
 * conversation is worse than one that asks again.
 */
function resolveInConversation(name: string, rows: ConversationRow[]): InChannel | null {
  const wanted = name.trim().toLowerCase();
  if (!wanted) return null;
  const toInChannel = (row: ConversationRow): InChannel => ({
    channelId: row.channelId,
    channelType: row.channelType,
    label: row.channel
      ? searchHitChannelLabel({ channelType: row.channelType, channelName: null }, row.channel)
      : `#${row.label}`,
  });
  const exact = rows.filter((r) => r.label.toLowerCase() === wanted);
  if (exact.length > 0) return exact.length === 1 ? toInChannel(exact[0]) : null;
  const partial = rows.filter((r) => r.label.toLowerCase().includes(wanted));
  return partial.length === 1 ? toInChannel(partial[0]) : null;
}

/**
 * "Search all chats": people and conversations first, then messages — the
 * order Telegram, WhatsApp, Slack and Discord all use, because a name typed
 * into search is far more often a person to open than a word to find.
 *
 * Three sections:
 * 1. **Conversations** — the loaded chat list matched locally (instant) merged
 *    with the server's search of the user's named channels (so a conversation
 *    outside the loaded first page is still found).
 * 2. **People** — the directory, by username or display-name prefix; a person
 *    the user already has a DM with reads "Open conversation".
 * 3. **Messages** — the cross-conversation message search, with the From /
 *    Match / Has / Date controls and the typed operators that fill them. An
 *    `in:` operator narrows it to one conversation, resolved against the
 *    loaded list AND a server search for that name.
 *
 * One debounce (here) feeds every section. Mount it only while open so each
 * visit starts clean.
 */
export function AllChatsSearchPanel({
  open,
  onOpenChange,
  initialQuery = '',
  channels,
  guilds,
  resolveChannelLabel,
  onSelectResult,
  onSelectConversation,
  onSelectPerson,
}: AllChatsSearchPanelProps) {
  const {
    rawQuery,
    setRawQuery,
    senderFilter,
    setSenderFilter,
    matchMode,
    setMatchMode,
    contentFilter,
    setContentFilter,
    dayRange,
    setDayRange,
    inConversation,
    setInConversation,
    ready,
    filters,
  } = useChatSearchControls(GLOBAL_OPERATORS, initialQuery);
  const query = useDebouncedValue(rawQuery, CHAT_SEARCH_DEBOUNCE_MS);
  const trimmed = query.trim();
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [showAllPeople, setShowAllPeople] = useState(false);
  useEffect(() => {
    setShowAllConversations(false);
    setShowAllPeople(false);
  }, [trimmed]);

  const guildNameById = useMemo(() => new Map(guilds.map((g) => [g.guildId, g.name])), [guilds]);

  // --- Conversations --------------------------------------------------------
  const conversationSearch = useChatConversationSearch(query, open);
  const conversationRows = useMemo<ConversationRow[]>(() => {
    if (trimmed.length === 0) return [];
    const rows = filterChannelsByQuery(channels, guilds, trimmed).map((c) =>
      conversationRowFromChannel(c, guildNameById),
    );
    const seen = new Set(rows.map((r) => r.channelId));
    for (const hit of conversationSearch.results) {
      if (seen.has(hit.channelId)) continue;
      seen.add(hit.channelId);
      rows.push(conversationRowFromHit(hit, guildNameById));
    }
    return rows;
  }, [trimmed, channels, guilds, guildNameById, conversationSearch.results]);

  // --- People ----------------------------------------------------------------
  const peopleSearch = useChatPeopleSearch(query, open);
  const dmPartnerIdsShown = useMemo(
    () => new Set(conversationRows.map((r) => r.channel?.dmPartnerId).filter(Boolean)),
    [conversationRows],
  );
  // Someone already listed as a conversation is not listed again as a person.
  const peopleRows = useMemo(
    () => peopleSearch.results.filter((p) => !dmPartnerIdsShown.has(p.userId)),
    [peopleSearch.results, dmPartnerIdsShown],
  );

  // --- Messages --------------------------------------------------------------
  // `in:#name` narrows the message search to one conversation. The name is
  // resolved against the loaded list and against its OWN server search (the
  // section above searches the box's remaining text, which is a different
  // string), so a conversation outside the loaded page still resolves.
  const inSearch = useChatConversationSearch(inConversation ?? '', open && !!inConversation);
  const inChannel = useMemo(() => {
    if (!inConversation) return null;
    const rows = channels.map((c) => conversationRowFromChannel(c, guildNameById));
    const seen = new Set(rows.map((r) => r.channelId));
    for (const hit of inSearch.results) {
      if (!seen.has(hit.channelId)) rows.push(conversationRowFromHit(hit, guildNameById));
    }
    return resolveInConversation(inConversation, rows);
  }, [inConversation, channels, guildNameById, inSearch.results]);
  const inResolving = !!inConversation && !inChannel && inSearch.isLoading;
  const inUnresolved = !!inConversation && !inChannel && !inResolving;

  const minLength = chatSearchMinQueryLength(matchMode);
  const hasTerms = trimmed.length >= minLength;
  const messagesQueryable =
    (hasTerms || !!senderFilter || !!contentFilter) && ready && !inUnresolved && !inResolving;
  const isDebouncing = rawQuery.trim() !== trimmed;

  const global = useChatGlobalSearch(query, filters, open && messagesQueryable && !inChannel);
  const scoped = useChatMessageSearch(
    inChannel?.channelId ?? null,
    query,
    filters,
    open && messagesQueryable && !!inChannel,
  );
  // The per-channel search returns messages without conversation fields; the
  // resolved `in:` conversation supplies them so one list renders both shapes.
  // The per-channel route reports an exact total, never a capped one.
  const messages = inChannel
    ? {
        results: scoped.results.map<ChatMessageSearchHitResponse>((m) => ({
          messageId: m.messageId,
          channelId: m.channelId,
          channelName: inChannel.label,
          channelType: inChannel.channelType,
          senderId: m.senderId,
          senderName: m.senderName ?? '',
          content: m.content,
          messageType: m.messageType,
          createdAt: m.createdAt,
        })),
        total: scoped.total,
        totalCapped: false,
        isLoading: scoped.isLoading,
        isError: scoped.isError,
        errorKind: scoped.errorKind,
        hasNextPage: scoped.hasNextPage,
        isFetchingNextPage: scoped.isFetchingNextPage,
        fetchNextPage: scoped.fetchNextPage,
      }
    : global;

  const terms = useMemo(
    () => searchTermsForHighlight(trimmed, hasTerms, matchMode),
    [trimmed, hasTerms, matchMode],
  );

  const sectionsLoading =
    (conversationSearch.isSearchable && conversationSearch.isLoading) ||
    (peopleSearch.isSearchable && peopleSearch.isLoading);
  const showConversations = conversationRows.length > 0;
  const showPeople = peopleRows.length > 0;
  const showMessagesHeading = messagesQueryable || inUnresolved || inResolving;
  const nothingToShow =
    !showConversations && !showPeople && !showMessagesHeading && !sectionsLoading;

  const visibleConversations = showAllConversations
    ? conversationRows
    : conversationRows.slice(0, SECTION_PREVIEW_SIZE);
  const visiblePeople = showAllPeople ? peopleRows : peopleRows.slice(0, SECTION_PREVIEW_SIZE);
  const liveText = rawQuery.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-primary flex-shrink-0" />
          <DialogTitle className="text-base font-semibold">Search all chats</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Find people and conversations by name, and search every conversation you are in by message
          text, sender, content, or date.
        </DialogDescription>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              maxLength={CHAT_SEARCH_MAX_QUERY_LENGTH}
              autoFocus
              placeholder="Search people, conversations and messages"
              aria-label="Search people, conversations and messages"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm pointer-coarse:text-base outline-none focus:ring-2 focus:ring-primary/40 [&::-webkit-search-cancel-button]:hidden"
            />
            {rawQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {inConversation && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="text-muted-foreground flex-shrink-0">In</span>
              <span
                className={[
                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5',
                  inUnresolved
                    ? 'border-destructive/50 text-destructive'
                    : 'border-border text-foreground',
                ].join(' ')}
              >
                <Hash className="h-3 w-3" aria-hidden="true" />
                {inChannel?.label.replace(/^#/, '') ?? inConversation}
                <button
                  type="button"
                  onClick={() => setInConversation(null)}
                  aria-label="Search all conversations"
                  className="ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              {inUnresolved && (
                <span role="alert" className="text-destructive">
                  No conversation named “{inConversation}”
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">From</span>
            {/* No roster spans every conversation, so there are no suggestions —
                the filter takes a typed handle. */}
            <SenderFilter members={[]} value={senderFilter} onChange={setSenderFilter} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Match</span>
            <SearchMatchModeToggle value={matchMode} onChange={setMatchMode} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Has</span>
            <SearchHasFilter value={contentFilter} onChange={setContentFilter} />
          </div>
          <div className="mt-2">
            <SearchDateFilter value={dayRange} onChange={setDayRange} />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border">
          {showConversations && (
            <section aria-label="Conversations" className="py-1">
              <h3 className="px-4 py-1 text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
                Conversations
              </h3>
              <ul>
                {visibleConversations.map((row) => (
                  <li key={row.channelId}>
                    <DialogClose asChild>
                      <button
                        type="button"
                        onClick={() => onSelectConversation(row.channelId)}
                        className="w-full flex items-center gap-3 text-left px-4 py-2 hover:bg-muted/60 transition-colors"
                      >
                        {row.channel ? (
                          <ChannelAvatar
                            channel={row.channel}
                            displayName={row.label}
                            sizeClassName="h-8 w-8"
                            textClassName="text-xs"
                            fallbackGlyph={row.label.charAt(0).toUpperCase() || '#'}
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                            style={{ background: seededGradient(row.label) }}
                          >
                            {row.label.charAt(0).toUpperCase() || '#'}
                          </span>
                        )}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {row.label}
                          </span>
                          {row.subtitle && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {row.subtitle}
                            </span>
                          )}
                        </span>
                      </button>
                    </DialogClose>
                  </li>
                ))}
              </ul>
              {conversationRows.length > SECTION_PREVIEW_SIZE && !showAllConversations && (
                <button
                  type="button"
                  onClick={() => setShowAllConversations(true)}
                  className="w-full px-4 py-1.5 text-left text-xs text-primary hover:bg-muted/60"
                >
                  Show all {conversationRows.length} conversations
                </button>
              )}
            </section>
          )}

          {showPeople && (
            <section aria-label="People" className="py-1 border-t border-border">
              <h3 className="px-4 py-1 text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
                People
              </h3>
              <ul>
                {visiblePeople.map((person) => {
                  const name = person.displayName || person.username;
                  return (
                    <li key={person.userId}>
                      <DialogClose asChild>
                        <button
                          type="button"
                          onClick={() => onSelectPerson(person)}
                          className="w-full flex items-center gap-3 text-left px-4 py-2 hover:bg-muted/60 transition-colors"
                        >
                          <UserAvatar
                            userId={person.userId}
                            displayName={name}
                            initial={name.charAt(0).toUpperCase()}
                            className="h-8 w-8"
                            fallbackClassName="text-xs font-semibold text-white"
                            background={seededGradient(name)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-foreground">
                              {name}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              @{person.username}
                            </span>
                          </span>
                          <span className="flex-shrink-0 text-xs text-muted-foreground">
                            {person.existingChannelId
                              ? 'Open conversation'
                              : 'Start a conversation'}
                          </span>
                        </button>
                      </DialogClose>
                    </li>
                  );
                })}
              </ul>
              {peopleRows.length > SECTION_PREVIEW_SIZE && !showAllPeople && (
                <button
                  type="button"
                  onClick={() => setShowAllPeople(true)}
                  className="w-full px-4 py-1.5 text-left text-xs text-primary hover:bg-muted/60"
                >
                  Show all {peopleRows.length} people
                </button>
              )}
            </section>
          )}

          {(showMessagesHeading || nothingToShow || sectionsLoading) && (
            <section
              aria-label="Messages"
              className={showConversations || showPeople ? 'border-t border-border' : undefined}
            >
              {showMessagesHeading && (
                <h3 className="px-4 pt-2 pb-1 text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
                  Messages
                  {messagesQueryable &&
                    !messages.isLoading &&
                    !messages.isError &&
                    !isDebouncing && (
                      <span className="ml-2 normal-case tracking-normal">
                        {messages.total}
                        {messages.totalCapped ? '+' : ''}{' '}
                        {messages.total === 1 && !messages.totalCapped ? 'match' : 'matches'}
                      </span>
                    )}
                </h3>
              )}
              {nothingToShow ? (
                // Keyed on what is in the box right now, not the debounced text,
                // so the hint keeps up with typing.
                <div className="text-center py-10 px-6">
                  <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {liveText.length === 0
                      ? 'Type a name to find a person or conversation'
                      : `Type at least ${minLength} characters to search messages`}
                  </p>
                  {liveText.length === 0 && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Or at least {minLength} characters to search messages in every conversation
                      you are in.
                    </p>
                  )}
                  {matchMode === 'contains' && liveText.length > 0 && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      For shorter words, switch to “Whole word”.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Or enter a username under “From”, or pick a content filter under “Has”.
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">{OPERATOR_HINT}</p>
                </div>
              ) : !messagesQueryable ? (
                sectionsLoading || inResolving ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Searching…</p>
                ) : null
              ) : messages.isLoading || isDebouncing ? (
                <p className="text-sm text-muted-foreground text-center py-10">Searching…</p>
              ) : messages.isError ? (
                <SearchErrorState kind={messages.errorKind} />
              ) : messages.results.length === 0 ? (
                <div className="text-center py-10 px-6">
                  <p className="text-sm text-muted-foreground">No messages found</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {hasTerms
                      ? NO_MATCH_HINT[matchMode]
                      : senderFilter
                        ? 'They haven’t posted in any of your conversations.'
                        : 'Nothing in your conversations matches that filter.'}
                  </p>
                </div>
              ) : (
                <ul className="py-1">
                  {messages.results.map((hit) => (
                    <li key={hit.messageId}>
                      <DialogClose asChild>
                        <button
                          type="button"
                          onClick={() => onSelectResult(hit)}
                          className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                        >
                          <MessageSearchResultRow
                            senderName={hit.senderName || hit.senderId}
                            content={hit.content}
                            createdAt={hit.createdAt}
                            terms={terms}
                            subtitle={inChannel ? inChannel.label : resolveChannelLabel(hit)}
                          />
                        </button>
                      </DialogClose>
                    </li>
                  ))}
                </ul>
              )}
              {messagesQueryable && messages.hasNextPage && (
                <div className="p-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => messages.fetchNextPage()}
                    disabled={messages.isFetchingNextPage}
                    className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {messages.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
